import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { PHONE_NUMBER_REGEX } from '@/lib/validations/user-preferences'
import { createInitialPreferences } from '@/data/user-preferences'
import { useUser } from '@/hooks/useUser'
import { useQueryClient } from '@tanstack/react-query'

export const Route = createFileRoute('/_authenticated/initial-preferences')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user } = useUser()
  const queryClient = useQueryClient()
  const router = useRouter()
  const [zipcode, setZipcode] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<{
    zipcode?: string
    phoneNumber?: string
  }>({})

  const handleSubmit = async (skipped: boolean = false) => {
    setIsSaving(true)
    setErrors({})

    try {
      if (!skipped) {
        // Validate fields if not skipped
        const hasZipcode = zipcode.trim() !== ''
        const hasPhoneNumber = phoneNumber.trim() !== ''

        if (hasZipcode && !/^\d{5}$/.test(zipcode)) {
          setErrors((prev) => ({
            ...prev,
            zipcode: 'Please enter a valid 5-digit zipcode',
          }))
          setIsSaving(false)
          return
        }

        if (hasPhoneNumber && !PHONE_NUMBER_REGEX.test(phoneNumber)) {
          setErrors((prev) => ({
            ...prev,
            phoneNumber: 'Please enter a valid phone number',
          }))
          setIsSaving(false)
          return
        }
      }

      await createInitialPreferences({
        userId: user!.id,
        zipcode: skipped ? '' : zipcode,
        phoneNumber: skipped ? '' : phoneNumber,
        skipped,
      })

      // Navigate to dashboard after successful save
      await queryClient.invalidateQueries({ queryKey: ['user-preferences'] })
      router.navigate({ to: '/dashboard' })
    } catch (error) {
      console.error('Error saving preferences:', error)
      setIsSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Welcome to Snowspot!</h1>
          <p className="text-muted-foreground">
            Help us personalize your experience
          </p>
        </div>

        <div className="bg-card rounded-lg border p-6 space-y-4">
          <div className="space-y-2">
            <label htmlFor="zipcode" className="text-sm font-medium">
              Zipcode (optional)
            </label>
            <input
              id="zipcode"
              type="text"
              placeholder="59715"
              value={zipcode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 5)
                setZipcode(value)
                if (errors.zipcode) {
                  setErrors((prev) => ({ ...prev, zipcode: undefined }))
                }
              }}
              maxLength={5}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.zipcode ? 'border-red-500' : 'border-input'
              } bg-background`}
              disabled={isSaving}
            />
            {errors.zipcode && (
              <p className="text-xs text-red-500">{errors.zipcode}</p>
            )}
            {!errors.zipcode && (
              <p className="text-xs text-muted-foreground">
                We'll find nearby SNOTEL stations and ski resorts for you
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="phoneNumber" className="text-sm font-medium">
              Phone Number (optional)
            </label>
            <input
              id="phoneNumber"
              type="tel"
              placeholder="(555) 555-5555"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value)
                if (errors.phoneNumber) {
                  setErrors((prev) => ({ ...prev, phoneNumber: undefined }))
                }
              }}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.phoneNumber ? 'border-red-500' : 'border-input'
              } bg-background`}
              disabled={isSaving}
            />
            {errors.phoneNumber && (
              <p className="text-xs text-red-500">{errors.phoneNumber}</p>
            )}
            {!errors.phoneNumber && (
              <p className="text-xs text-muted-foreground">
                Get notifications about snowfall in your favorite spots
              </p>
            )}
          </div>

          <div className="space-y-2 pt-2">
            <button
              onClick={() => handleSubmit(false)}
              disabled={isSaving || (!zipcode && !phoneNumber)}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSaving ? 'Saving...' : 'Continue'}
            </button>

            <button
              onClick={() => handleSubmit(true)}
              disabled={isSaving}
              className="w-full px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Skip for now
            </button>
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          You can always update these settings later in your preferences
        </p>
      </div>
    </div>
  )
}
